/**
 * VISTOERA_CANVAS_GENERATION_AND_ASSET_NODE_UNIFIED_MINIMAL_STATIC_SHELL_AND_EXPANSION_CONTRACT_V2
 *
 * Canvas-wide visual / interaction contract:
 * - Collapsed = minimal header (icon + title) + result/asset only
 * - Expanded = Result First + editing sections (Canvas-level single expand)
 * - Result click expands; simple select / drag / media controls do not
 */

/** React Flow `node.type` families that use Generation Node expand/collapse. */
export const GENERATION_EXPAND_FAMILIES = [
  'generator',
  'video_generator',
  'voice_generator',
  'audio_generator',
  'tryon_generator',
  'apparel_video_generator',
  'motion_processor',
  'generate_creative',
  'script_generation',
  'storyboard_generation',
  'text_generator',
] as const

export type GenerationExpandFamily = (typeof GENERATION_EXPAND_FAMILIES)[number]

export function isGenerationExpandFamily(family: string | undefined | null): boolean {
  return Boolean(family && (GENERATION_EXPAND_FAMILIES as readonly string[]).includes(family))
}

/**
 * Families whose React Flow hit bounds must track live content geometry
 * (Header + Result when collapsed; + Inspector when expanded).
 *
 * Persisted / explicit `node.width`/`node.height` must NOT lock the
 * `.react-flow__node` wrapper — that creates collapsed ghost hit areas
 * where VISUAL_BOUNDS != DOM_HIT_BOUNDS (Canvas blank still routes to
 * Node Context Menu).
 *
 * Includes `video_media` because Shell V2 expand/collapse changes its
 * content height the same way generation cards do.
 *
 * VistoeraTASK-00171 — `audio_asset` joins for the identical reason:
 * its body height grows when the Footer edit action appears on expand,
 * so a stale persisted/locked RF wrapper height would create the same
 * ghost-hit-area bug this function exists to prevent.
 */
export function usesContentMeasuredNodeGeometry(family: string | undefined | null): boolean {
  return (
    isGenerationExpandFamily(family) ||
    family === 'video_media' ||
    family === 'text_asset' ||
    family === 'audio_asset'
  )
}

/** Asset / media families that share the minimal static shell (not generation expand). */
export const ASSET_MINIMAL_SHELL_FAMILIES = [
  'media',
  'video_media',
  'text_asset',
  'motion_asset',
  'audio_asset',
] as const

export function isAssetMinimalShellFamily(family: string | undefined | null): boolean {
  return Boolean(family && (ASSET_MINIMAL_SHELL_FAMILIES as readonly string[]).includes(family))
}

/** Shell CSS modifiers — collapsed vs expanded geometry. */
export function canvasNodeShellStateClass(isExpanded: boolean): string {
  return isExpanded ? 'is-expanded' : 'is-collapsed'
}

/** Shared expanded width for canonical Generation Cards — use `generationNodeWidthPresets`. */
export { GENERATION_CARD_EXPANDED_WIDTH_PX } from './generationNodeWidthPresets'

/** Result / media body click should expand — not header, not controls. */
export const NODE_EXPAND_TRIGGER_CLASS = 'pcv-node-expand-trigger'

/** Player controls that must not expand the node. */
export const MEDIA_INTERACTION_CONTROL_CLASS = 'pcv-media-interaction-control nodrag'
